import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, getCurrency, initializeCurrency } from './utils/currency';
import { searchAirports } from './services/flightApiService';
import './BookFlightPage.css';
import './AirportSuggestions.css';

const InternationalFlightsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tripType, setTripType] = useState('round-trip');
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    passengers: {
      adults: 1,
      children: 0,
      infants: 0
    },
    class: 'economy'
  });
  const [multiCityRoutes, setMultiCityRoutes] = useState([
    { from: '', to: '', date: '' }
  ]);
  const [currency, setCurrency] = useState(null);
  const [autoSearch, setAutoSearch] = useState(false);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [multiCitySuggestions, setMultiCitySuggestions] = useState({});
  const [showMultiCitySuggestions, setShowMultiCitySuggestions] = useState({});
  const searchTimeoutRef = useRef(null);

  // Handle prefill data from navigation
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    if (prefillData) {
      if (prefillData.tripType) {
        setTripType(prefillData.tripType);
      }
      setFormData(prev => ({
        ...prev,
        from: prefillData.from || prev.from,
        to: prefillData.to || prev.to,
        departureDate: prefillData.departureDate || prev.departureDate,
        returnDate: prefillData.returnDate || prev.returnDate
      }));
      if (prefillData.from && prefillData.to && prefillData.departureDate) {
        setAutoSearch(true);
      }
    }
  }, [location.state]);

  // Initialize currency
  useEffect(() => {
    const initCurrency = async () => {
      try {
        const detectedCurrency = await initializeCurrency();
        setCurrency(detectedCurrency);
      } catch (error) {
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    const handleCurrencyInit = (event) => {
      setCurrency(event.detail);
    };
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  // Nigerian airports (for departure)
  const nigerianAirports = [
    { code: 'LOS', name: 'Lagos (LOS)', city: 'Lagos' },
    { code: 'ABV', name: 'Abuja (ABV)', city: 'Abuja' },
    { code: 'PHC', name: 'Port Harcourt (PHC)', city: 'Port Harcourt' },
    { code: 'KAN', name: 'Kano (KAN)', city: 'Kano' },
    { code: 'ENU', name: 'Enugu (ENU)', city: 'Enugu' }
  ];

  // International airports (for destination)
  const internationalAirports = [
    { code: 'LHR', name: 'London Heathrow (LHR)', city: 'London', country: 'UK' },
    { code: 'LGW', name: 'London Gatwick (LGW)', city: 'London', country: 'UK' },
    { code: 'DXB', name: 'Dubai (DXB)', city: 'Dubai', country: 'UAE' },
    { code: 'AUH', name: 'Abu Dhabi (AUH)', city: 'Abu Dhabi', country: 'UAE' },
    { code: 'JFK', name: 'New York JFK (JFK)', city: 'New York', country: 'USA' },
    { code: 'LAX', name: 'Los Angeles (LAX)', city: 'Los Angeles', country: 'USA' },
    { code: 'ACC', name: 'Accra (ACC)', city: 'Accra', country: 'Ghana' },
    { code: 'JNB', name: 'Johannesburg (JNB)', city: 'Johannesburg', country: 'South Africa' },
    { code: 'CDG', name: 'Paris Charles de Gaulle (CDG)', city: 'Paris', country: 'France' },
    { code: 'IST', name: 'Istanbul (IST)', city: 'Istanbul', country: 'Turkey' },
    { code: 'FRA', name: 'Frankfurt (FRA)', city: 'Frankfurt', country: 'Germany' },
    { code: 'AMS', name: 'Amsterdam (AMS)', city: 'Amsterdam', country: 'Netherlands' },
    { code: 'MAD', name: 'Madrid (MAD)', city: 'Madrid', country: 'Spain' },
    { code: 'FCO', name: 'Rome (FCO)', city: 'Rome', country: 'Italy' },
    { code: 'CAI', name: 'Cairo (CAI)', city: 'Cairo', country: 'Egypt' },
    { code: 'ADD', name: 'Addis Ababa (ADD)', city: 'Addis Ababa', country: 'Ethiopia' },
    { code: 'NBO', name: 'Nairobi (NBO)', city: 'Nairobi', country: 'Kenya' },
    { code: 'CPT', name: 'Cape Town (CPT)', city: 'Cape Town', country: 'South Africa' },
    { code: 'DOH', name: 'Doha (DOH)', city: 'Doha', country: 'Qatar' },
    { code: 'BKK', name: 'Bangkok (BKK)', city: 'Bangkok', country: 'Thailand' },
    { code: 'SIN', name: 'Singapore (SIN)', city: 'Singapore', country: 'Singapore' },
    { code: 'HKG', name: 'Hong Kong (HKG)', city: 'Hong Kong', country: 'Hong Kong' }
  ];

  // International routes
  const popularRoutes = [
    { from: 'Lagos', to: 'London', fromCode: 'LOS', toCode: 'LHR', priceNGN: 680000, airline: '✈️' },
    { from: 'Abuja', to: 'Dubai', fromCode: 'ABV', toCode: 'DXB', priceNGN: 420000, airline: '✈️' },
    { from: 'Lagos', to: 'New York', fromCode: 'LOS', toCode: 'JFK', priceNGN: 850000, airline: '✈️' },
    { from: 'Lagos', to: 'Accra', fromCode: 'LOS', toCode: 'ACC', priceNGN: 95000, airline: '✈️' },
    { from: 'Lagos', to: 'Johannesburg', fromCode: 'LOS', toCode: 'JNB', priceNGN: 580000, airline: '✈️' },
    { from: 'Abuja', to: 'Paris', fromCode: 'ABV', toCode: 'CDG', priceNGN: 720000, airline: '✈️' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Search airports when typing in from/to fields (with debounce)
    if (name === 'from') {
      if (value.length >= 2) {
        setShowFromSuggestions(true);
        searchTimeoutRef.current = setTimeout(() => {
          searchAirportsForField(value, 'from');
        }, 300);
      } else {
        setFromSuggestions([]);
        setShowFromSuggestions(false);
      }
    }

    if (name === 'to') {
      if (value.length >= 2) {
        setShowToSuggestions(true);
        searchTimeoutRef.current = setTimeout(() => {
          searchAirportsForField(value, 'to');
        }, 300);
      } else {
        setToSuggestions([]);
        setShowToSuggestions(false);
      }
    }
  };

  const searchAirportsForField = async (query, field) => {
    try {
      const result = await searchAirports(query);
      if (result.success && result.data) {
        if (field === 'from') {
          setFromSuggestions(result.data);
          setShowFromSuggestions(true);
        } else {
          setToSuggestions(result.data);
          setShowToSuggestions(true);
        }
      } else {
        if (field === 'from') {
          setFromSuggestions([]);
        } else {
          setToSuggestions([]);
        }
      }
    } catch (error) {
      console.error('Error searching airports:', error);
      if (field === 'from') {
        setFromSuggestions([]);
      } else {
        setToSuggestions([]);
      }
    }
  };

  const selectAirport = (airport, field) => {
    const displayValue = airport.iataCode 
      ? `${airport.address?.cityName || airport.name} (${airport.iataCode})`
      : airport.name;
    
    setFormData(prev => ({
      ...prev,
      [field]: displayValue
    }));

    if (field === 'from') {
      setShowFromSuggestions(false);
      setFromSuggestions([]);
    } else {
      setShowToSuggestions(false);
      setToSuggestions([]);
    }
  };

  const handlePassengerChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      passengers: {
        ...prev.passengers,
        [type]: parseInt(value) || 0
      }
    }));
  };

  const handleAddMultiCityRoute = () => {
    setMultiCityRoutes(prev => [...prev, { from: '', to: '', date: '' }]);
  };

  const handleMultiCityChange = (index, field, value) => {
    const updated = [...multiCityRoutes];
    updated[index][field] = value;
    setMultiCityRoutes(updated);

    // Search airports for multi-city routes
    if ((field === 'from' || field === 'to') && value.length >= 2) {
      searchAirportsForMultiCity(value, index, field);
    } else if ((field === 'from' || field === 'to')) {
      setShowMultiCitySuggestions(prev => ({
        ...prev,
        [`${index}-${field}`]: false
      }));
    }
  };

  const searchAirportsForMultiCity = async (query, index, field) => {
    const key = `${index}-${field}`;
    try {
      const result = await searchAirports(query);
      if (result.success && Array.isArray(result.data)) {
        setMultiCitySuggestions(prev => ({
          ...prev,
          [key]: result.data,
        }));
        setShowMultiCitySuggestions(prev => ({
          ...prev,
          [key]: true,
        }));
      } else {
        setMultiCitySuggestions(prev => ({ ...prev, [key]: [] }));
      }
    } catch (error) {
      console.error('Error searching airports:', error);
      setMultiCitySuggestions(prev => ({ ...prev, [key]: [] }));
    }
  };

  const selectAirportMultiCity = (airport, index, field) => {
    const displayValue = airport.iataCode 
      ? `${airport.address?.cityName || airport.name} (${airport.iataCode})`
      : airport.name;
    
    const updated = [...multiCityRoutes];
    updated[index][field] = displayValue;
    setMultiCityRoutes(updated);

    setShowMultiCitySuggestions(prev => ({
      ...prev,
      [`${index}-${field}`]: false
    }));
    setMultiCitySuggestions(prev => ({
      ...prev,
      [`${index}-${field}`]: []
    }));
  };

  const handleRemoveMultiCityRoute = (index) => {
    if (multiCityRoutes.length > 1) {
      setMultiCityRoutes(prev => prev.filter((_, i) => i !== index));
    }
  };

  const performSearch = () => {
    const totalPassengers = formData.passengers.adults + formData.passengers.children + formData.passengers.infants;
    const passengersString = totalPassengers.toString();
    // Use Duffel's exact cabin class values (already in correct format)
    const travelClass = formData.class;
    
    const finalSearchData = {
      tripType,
      from: formData.from, // Airport code (e.g., "LOS")
      to: formData.to, // Airport code (e.g., "LHR")
      departureDate: formData.departureDate,
      returnDate: formData.returnDate || '',
      passengers: passengersString,
      travelClass: travelClass,
      multiCityRoutes: tripType === 'multi-city' ? multiCityRoutes : null,
      flightType: 'international' // Mark as international flight
    };

    console.log('🔍 International Flight Search:', finalSearchData);
    console.log('📡 Will call Supabase Edge Function API via FlightResultsPage');
    
    navigate('/results', { state: { searchData: finalSearchData } });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
  };

  useEffect(() => {
    if (autoSearch && formData.from && formData.to && formData.departureDate) {
      const timer = setTimeout(() => {
        performSearch();
        setAutoSearch(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [autoSearch, formData.from, formData.to, formData.departureDate, formData.passengers, formData.class, tripType, multiCityRoutes, navigate]);

  const handlePopularRouteClick = (route) => {
    setFormData(prev => ({
      ...prev,
      from: route.fromCode,
      to: route.toCode
    }));
    const bookingForm = document.getElementById('booking-form');
    if (bookingForm) {
      bookingForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getTotalPassengers = () => {
    return formData.passengers.adults + formData.passengers.children + formData.passengers.infants;
  };

  // Group international airports by region
  const groupedAirports = {
    'Europe': internationalAirports.filter(a => ['LHR', 'LGW', 'CDG', 'FRA', 'AMS', 'MAD', 'FCO', 'IST'].includes(a.code)),
    'Middle East': internationalAirports.filter(a => ['DXB', 'AUH', 'DOH'].includes(a.code)),
    'Africa': internationalAirports.filter(a => ['ACC', 'JNB', 'CPT', 'CAI', 'ADD', 'NBO'].includes(a.code)),
    'Americas': internationalAirports.filter(a => ['JFK', 'LAX'].includes(a.code)),
    'Asia': internationalAirports.filter(a => ['BKK', 'SIN', 'HKG'].includes(a.code))
  };

  return (
    <div className="book-flight-page">
      <Header />
      
      <section className="booking-hero-section">
        <div className="page-container">
          <div className="booking-hero-content">
            <h1 className="booking-hero-title">International Flights</h1>
            <p className="booking-hero-subtitle">
              Book flights from Nigeria to destinations worldwide - Best prices guaranteed.
            </p>
          </div>
        </div>
      </section>

      <section id="booking-form" className="booking-form-section">
        <div className="page-container">
          <div className="booking-form-card">
            <div className="trip-type-toggle">
              <button
                type="button"
                className={`trip-type-button ${tripType === 'one-way' ? 'active' : ''}`}
                onClick={() => setTripType('one-way')}
              >
                One-Way
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'round-trip' ? 'active' : ''}`}
                onClick={() => setTripType('round-trip')}
              >
                Round-Trip
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'multi-city' ? 'active' : ''}`}
                onClick={() => setTripType('multi-city')}
              >
                Multi-City
              </button>
            </div>

            <form onSubmit={handleSearch} className="booking-form">
              {(tripType === 'one-way' || tripType === 'round-trip') && (
                <div className="form-fields-grid">
                  <div className="form-field-card" style={{ position: 'relative' }}>
                    <label className="field-label">
                      <span className="field-icon">📍</span>
                      From
                    </label>
                    <input
                      type="text"
                      name="from"
                      value={formData.from}
                      onChange={handleInputChange}
                      onFocus={() => {
                        if (formData.from.length >= 2 && fromSuggestions.length > 0) {
                          setShowFromSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowFromSuggestions(false), 250);
                      }}
                      placeholder="Type city or airport name"
                      className="form-input"
                      required
                    />
                    {showFromSuggestions && (
                      <div className="airport-suggestions">
                        {fromSuggestions.length > 0 ? (
                          fromSuggestions.map((airport, index) => (
                            <div
                              key={index}
                              className="suggestion-item"
                              onClick={() => selectAirport(airport, 'from')}
                            >
                              <div className="suggestion-main">
                                <strong>{airport.address?.cityName || airport.name}</strong>
                                {airport.iataCode && (
                                  <span className="suggestion-code">({airport.iataCode})</span>
                                )}
                              </div>
                              <div className="suggestion-sub">
                            {airport.name}
                                {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                            {formData.from.length >= 2 ? 'Searching airports...' : 'Type at least 2 characters to search'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-field-card" style={{ position: 'relative' }}>
                    <label className="field-label">
                      <span className="field-icon">✈️</span>
                      To
                    </label>
                    <input
                      type="text"
                      name="to"
                      value={formData.to}
                      onChange={handleInputChange}
                      onFocus={() => {
                        if (formData.to.length >= 2 && toSuggestions.length > 0) {
                          setShowToSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowToSuggestions(false), 250);
                      }}
                      placeholder="Type city or airport name"
                      className="form-input"
                      required
                    />
                    {showToSuggestions && (
                      <div className="airport-suggestions">
                        {toSuggestions.length > 0 ? (
                          toSuggestions.map((airport, index) => (
                            <div
                              key={index}
                              className="suggestion-item"
                              onClick={() => selectAirport(airport, 'to')}
                            >
                              <div className="suggestion-main">
                                <strong>{airport.address?.cityName || airport.name}</strong>
                                {airport.iataCode && (
                                  <span className="suggestion-code">({airport.iataCode})</span>
                                )}
                              </div>
                              <div className="suggestion-sub">
                              {airport.name}
                                {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                            {formData.to.length >= 2 ? 'Searching airports...' : 'Type at least 2 characters to search'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📅</span>
                      Departure Date
                    </label>
                    <input
                      type="date"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleInputChange}
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  {tripType === 'round-trip' && (
                    <div className="form-field-card">
                      <label className="field-label">
                        <span className="field-icon">📅</span>
                        Return Date
                      </label>
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={formData.departureDate || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  )}

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">👥</span>
                      Passengers
                    </label>
                    <div className="passengers-dropdown-wrapper">
                      <div className="passengers-display">
                        {getTotalPassengers()} {getTotalPassengers() === 1 ? 'Passenger' : 'Passengers'}
                      </div>
                      <div className="passengers-details">
                        <div className="passenger-type-row">
                          <span>Adults (12+)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('adults', Math.max(1, formData.passengers.adults - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.adults}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('adults', formData.passengers.adults + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="passenger-type-row">
                          <span>Children (2-11)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('children', Math.max(0, formData.passengers.children - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.children}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('children', formData.passengers.children + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="passenger-type-row">
                          <span>Infants (0-2)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('infants', Math.max(0, formData.passengers.infants - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.infants}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('infants', formData.passengers.infants + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">💺</span>
                      Class
                    </label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="economy">Economy</option>
                      <option value="premium_economy">Premium Economy</option>
                      <option value="business">Business</option>
                      <option value="first">First Class</option>
                    </select>
                  </div>
                </div>
              )}

              {tripType === 'multi-city' && (
                <div className="multi-city-form">
                  {multiCityRoutes.map((route, index) => (
                    <div key={index} className="multi-city-route">
                      <div className="multi-city-header">
                        <h3 className="route-number">Route {index + 1}</h3>
                        {multiCityRoutes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMultiCityRoute(index)}
                            className="remove-route-btn"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-fields-grid">
                        <div className="form-field-card" style={{ position: 'relative' }}>
                          <label className="field-label">
                            <span className="field-icon">📍</span>
                            From
                          </label>
                          <input
                            type="text"
                            value={route.from}
                            onChange={(e) => handleMultiCityChange(index, 'from', e.target.value)}
                            onFocus={() => {
                              if (route.from.length >= 2) {
                                setShowMultiCitySuggestions(prev => ({
                                  ...prev,
                                  [`${index}-from`]: true
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowMultiCitySuggestions(prev => ({
                                  ...prev,
                                  [`${index}-from`]: false
                                }));
                              }, 250);
                            }}
                            placeholder="Type city or airport name"
                            className="form-input"
                            required
                          />
                          {showMultiCitySuggestions[`${index}-from`] && multiCitySuggestions[`${index}-from`]?.length > 0 && (
                            <div className="airport-suggestions">
                              {multiCitySuggestions[`${index}-from`].map((airport, idx) => (
                                <div
                                  key={idx}
                                  className="suggestion-item"
                                  onClick={() => selectAirportMultiCity(airport, index, 'from')}
                                >
                                  <div className="suggestion-main">
                                    <strong>{airport.address?.cityName || airport.name}</strong>
                                    {airport.iataCode && (
                                      <span className="suggestion-code">({airport.iataCode})</span>
                                    )}
                                  </div>
                                  <div className="suggestion-sub">
                                  {airport.name}
                                    {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="form-field-card" style={{ position: 'relative' }}>
                          <label className="field-label">
                            <span className="field-icon">✈️</span>
                            To
                          </label>
                          <input
                            type="text"
                            value={route.to}
                            onChange={(e) => handleMultiCityChange(index, 'to', e.target.value)}
                            onFocus={() => {
                              if (route.to.length >= 2) {
                                setShowMultiCitySuggestions(prev => ({
                                  ...prev,
                                  [`${index}-to`]: true
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowMultiCitySuggestions(prev => ({
                                  ...prev,
                                  [`${index}-to`]: false
                                }));
                              }, 250);
                            }}
                            placeholder="Type city or airport name"
                            className="form-input"
                            required
                          />
                          {showMultiCitySuggestions[`${index}-to`] && multiCitySuggestions[`${index}-to`]?.length > 0 && (
                            <div className="airport-suggestions">
                              {multiCitySuggestions[`${index}-to`].map((airport, idx) => (
                                <div
                                  key={idx}
                                  className="suggestion-item"
                                  onClick={() => selectAirportMultiCity(airport, index, 'to')}
                                >
                                  <div className="suggestion-main">
                                    <strong>{airport.address?.cityName || airport.name}</strong>
                                    {airport.iataCode && (
                                      <span className="suggestion-code">({airport.iataCode})</span>
                                    )}
                                  </div>
                                  <div className="suggestion-sub">
                                {airport.name}
                                    {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                  </div>
                                </div>
                            ))}
                            </div>
                          )}
                        </div>
                        <div className="form-field-card">
                          <label className="field-label">
                            <span className="field-icon">📅</span>
                            Date
                          </label>
                          <input
                            type="date"
                            value={route.date}
                            onChange={(e) => handleMultiCityChange(index, 'date', e.target.value)}
                            className="form-input"
                            min={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddMultiCityRoute}
                    className="add-route-button"
                  >
                    + Add Another Route
                  </button>
                </div>
              )}

              <button type="submit" className="search-flights-button">
                Search Flights
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="popular-routes-section">
        <div className="page-container">
          <div className="popular-routes-header">
            <h2 className="popular-routes-title">Popular International Routes</h2>
            <p className="popular-routes-subtitle">
              Quick access to frequently booked international destinations
            </p>
          </div>
          <div className="popular-routes-grid">
            {popularRoutes.map((route, index) => (
              <div
                key={index}
                className="popular-route-card"
                onClick={() => handlePopularRouteClick(route)}
              >
                <div className="route-content">
                  <div className="route-header">
                    <span className="route-airline">{route.airline}</span>
                    <span className="route-arrow">→</span>
                  </div>
                  <h3 className="route-title">
                    {route.from} → {route.to}
                  </h3>
                  <p className="route-codes">
                    {route.fromCode} → {route.toCode}
                  </p>
                  <p className="route-price">From {formatPrice(route.priceNGN, { decimals: 0, currency: currency || getCurrency() })}</p>
                </div>
                <div className="route-action">
                  <span className="route-action-icon">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InternationalFlightsPage;
